<template>
  <basic-dialog :show="show" @close="$emit('close')">
    <template v-slot:title>{{ title }}</template>
    <v-row>
      <v-col class="flex-grow-1">
        <h1 class="subtitle-1 py-2">Available Users</h1>
        <v-card>
          <v-card-title>
            <v-row>
              <v-col
                ><agent-list-select
                  custom-label="Select Agent"
                  dense
                  v-model="selectedAgentId"
                ></agent-list-select
              ></v-col>
              <v-col
                ><v-text-field
                  label="Staff Quick Search"
                  hide-details
                  v-model="search"
                  :outlined="outlinedInputs"
                  dense
                  prepend-inner-icon="mdi-magnify"
                  :disabled="selectedAgentId === ''"
                ></v-text-field
              ></v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-switch
              v-model="userIsActiveFilter"
              label="User Is Active"
            ></v-switch>
            <v-data-table
              :headers="headers"
              :items="availableUsers"
              :search="search"
              :loading="$apollo.loading"
              v-model="addUsers"
              show-select
            ></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        style="flex: 0 0 60px"
        class="d-flex align-center justify-center flex-column"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              fab
              color="success"
              v-on="on"
              class="mb-2"
              :disabled="addUsersDisabled"
              @click="onAddUsers"
              :loading="addUsersLoading"
            >
              <v-icon dark>mdi-arrow-right</v-icon>
            </v-btn>
          </template>
          <span>Add To Access</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              small
              fab
              color="error"
              :disabled="removeUsersDisabled"
              @click="onRemoveUsers"
              :loading="removeUsersLoading"
            >
              <v-icon dark>mdi-arrow-left</v-icon>
            </v-btn>
          </template>
          <span>Remove Access</span>
        </v-tooltip>
      </v-col>
      <v-col class="flex-grow-1">
        <h1 class="subtitle-1 py-2">Users With Access</h1>
        <v-card>
          <v-card-title>
            <v-container fluid>
              <v-text-field
                label="Quick Search"
                hide-details
                v-model="searchUsersWithAccess"
                :outlined="outlinedInputs"
                dense
                prepend-inner-icon="mdi-magnify"
              ></v-text-field>
            </v-container>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="usersWithAccess"
              :search="searchUsersWithAccess"
              :loading="$apollo.loading"
              v-model="removeUsers"
              show-select
            ></v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <v-col>
        <code>
          <pre>{{ usersExternalForms }}</pre>
        </code>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import gql from "graphql-tag";
import AgentListSelect from "../AgentListSelect.vue";
import BasicDialog from "../BasicDialog.vue";
import { removeGQLTypename } from "../../../../helpers";
const LIST_QUERY = gql`
  query usersList($agentId: ID, $isActive: Boolean) {
    users(filter: { agent_id: $agentId, is_active_eq: $isActive }) {
      totalCount
      edges {
        node {
          id
          name
          username
          email
        }
      }
    }
  }
`;
const FORM_USERS_QUERY = gql`
  query formUsers($filter: UsersExternalFormsFilterType) {
    usersExternalForms(filter: $filter) {
      edges {
        node {
          id
          userId
          user {
            name
            username
            email
          }
        }
      }
    }
  }
`;
const UPDATE_ACCESS_MUTATION = gql`
  mutation updateExternalFormsAccess($input: ExternalFormsAccessInput!) {
    updateExternalFormsAccess(input: $input) {
      success
      error
      message
    }
  }
`;
const ADD_ACCESS_MUTATION = gql`
  mutation externalFormAddAccess($input: ExternalFormsAccessInput!) {
    externalFormAddAccess(input: $input) {
      success
      error
      message
    }
  }
`;
const REMOVE_ACCESS_MUTATION = gql`
  mutation externalFormRemoveAccess($input: ExternalFormsAccessInput!) {
    externalFormRemoveAccess(input: $input) {
      success
      error
      message
    }
  }
`;
export default {
  name: "external-form-access-dialog",
  components: { BasicDialog, AgentListSelect },
  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      debug: false,
      search: "",
      searchUsersWithAccess: "",
      headers: [
        {
          text: "Username",
          value: "username",
        },
        {
          text: "Email",
          value: "email",
        },
      ],
      addUsers: [],
      removeUsers: [],
      selectedAgentId: this.$store.getters.userConfig.ownerId,
      addUsersLoading: false,
      removeUsersLoading: false,
      userIsActiveFilter: true,
    };
  },

  computed: {
    availableUsers() {
      return this.users && this.users.edges
        ? this.users.edges
            .map(removeGQLTypename)
            .filter(this.availableUsersFilter)
        : [];
    },

    usersWithAccess() {
      return this.usersExternalForms && this.usersExternalForms.edges
        ? this.usersExternalForms.edges.map(removeGQLTypename).map((user) => {
            return {
              ...user,
              name: user.user.name,
              username: user.user.username,
              email: user.user.email,
            };
          })
        : [];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    title() {
      return this.value ? `${this.value.name} - Access` : "Access Form";
    },
    removeUsersDisabled() {
      return this.removeUsers.length === 0;
    },
    addUsersDisabled() {
      return this.addUsers.length === 0;
    },
  },
  methods: {
    availableUsersFilter(item) {
      return this.usersWithAccess.length > 0
        ? this.usersWithAccess.map((user) => user.userId).indexOf(item.id) ===
            -1
        : true;
    },
    onAddUsers() {
      const users = this.addUsers.map((user) => user.id);
      this.addUsersLoading = true;
      this.$apollo
        .mutate({
          mutation: ADD_ACCESS_MUTATION,
          variables: {
            input: { formCodes: [this.value.code], userIds: users },
          },
          refetchQueries: [
            {
              query: FORM_USERS_QUERY,
              variables: { filter: { formCode_eq: this.value.code } },
            },
          ],
        })
        .then((res) => {
          this.$store.dispatch("showAppSnackbar", {
            message: "Access added",
            color: "success",
          });
          this.addUsersLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("showAppSnackbar", {
            message: "Error adding access",
            color: "error",
          });
          this.addUsersLoading = false;
        });
    },
    onRemoveUsers() {
      const users = this.removeUsers.map((user) => user.userId);
      this.removeUsersLoading = true;
      console.log(users);
      this.$apollo
        .mutate({
          mutation: REMOVE_ACCESS_MUTATION,
          variables: {
            input: { formCodes: [this.value.code], userIds: users },
          },
          refetchQueries: [
            {
              query: FORM_USERS_QUERY,
              variables: { filter: { formCode_eq: this.value.code } },
            },
          ],
        })
        .then((res) => {
          this.$store.dispatch("showAppSnackbar", {
            message: "Access removed",
            color: "success",
          });
          this.removeUsersLoading = false;
        })
        .catch((err) => {
          this.$store.dispatch("showAppSnackbar", {
            message: "Error removing access",
            color: "error",
          });
          this.removeUsersLoading = false;
        });
    },
  },

  apollo: {
    users: {
      query: LIST_QUERY,
      variables() {
        return {
          agentId: this.selectedAgentId,
          isActive: this.userIsActiveFilter,
        };
      },
      skip() {
        return this.selectedAgentId === "";
      },
      fetchPolicy: "network-only",
    },
    usersExternalForms: {
      query: FORM_USERS_QUERY,
      variables() {
        return {
          filter: { formCode_eq: this.value.code },
        };
      },
      skip() {
        return this.value == null;
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style>
</style>


// WEBPACK FOOTER //
// src/components/gt/shared/external-forms/ExternalFormAccessDialog.vue